import { Component, Input, OnChanges } from '@angular/core';

import { scoreColor } from '../analysis/score-color.util';

/**
 * Circular score gauge for the work environment survey.
 *
 * Replaces the MUI `Gauge` the AMIL prototype used. Geometry follows
 * `risk-gauge` on the dashboard — an SVG arc with the value written inside it —
 * so the two read as the same family, and the colour comes from the shared
 * `scoreColor` helper so it matches the question bars.
 *
 * `pathLength="100"` lets the filled portion be set with a dash offset in
 * percent, which keeps the arc maths out of this component for both shapes.
 */
@Component({
  selector: 'survey-score-gauge',
  templateUrl: './survey-score-gauge.component.html',
  styleUrls: ['./survey-score-gauge.component.scss'],
  standalone: false
})
export class SurveyScoreGaugeComponent implements OnChanges {
  /** The score to show. Null renders an empty gauge. */
  @Input() value: number | null = null;

  /** Top of the range. 5 for the answer scale, 100 for a percentage. */
  @Input() max = 5;

  /** 'semi' for the half-circle score gauges, 'full' for the response rate. */
  @Input() arc: 'semi' | 'full' = 'semi';

  /** Decimals in the value label. 1 for scores, 0 for percentages. */
  @Input() decimals = 1;

  /** Appended to the value, e.g. '%'. */
  @Input() unit = '';

  /** Small caption under the value, already translated. */
  @Input() caption = '';

  /** Font Awesome class for an icon above the value, e.g. 'fa-user'. */
  @Input() icon = '';

  /** Semicircle: left to right across the top. */
  private readonly semiPath = 'M 20 100 A 80 80 0 0 1 180 100';

  /** Full circle, drawn clockwise from the top as two half arcs. */
  private readonly fullPath =
    'M 100 20 A 80 80 0 0 1 100 180 A 80 80 0 0 1 100 20';

  hasValue = false;
  valueText = '';
  arcColor = '';

  /** Percentage of the arc to fill, expressed as a dash offset. */
  dashOffset = 100;

  ngOnChanges(): void {
    this.hasValue = this.value !== null && !isNaN(this.value);

    const ratio = this.hasValue
      ? Math.min(1, Math.max(0, (this.value as number) / this.max))
      : 0;

    this.dashOffset = 100 - ratio * 100;
    this.arcColor = scoreColor(this.hasValue ? this.value : null, this.max);
    this.valueText = this.hasValue
      ? `${(this.value as number).toFixed(this.decimals)}${this.unit}`
      : '—';
  }

  get arcPath(): string {
    return this.arc === 'full' ? this.fullPath : this.semiPath;
  }

  get viewBox(): string {
    return this.arc === 'full' ? '0 0 200 200' : '0 0 200 118';
  }

  /** Vertical centre of the text block, which differs between the two shapes. */
  get textY(): number {
    return this.arc === 'full' ? 100 : 88;
  }
}
